import { css, type Theme } from "@emotion/react";

import * as animations from "@styles/emotion-styles/animations";
import * as dimensions from "@styles/emotion-styles/dimensions";
import { removeClickOutline } from "@styles/emotion-styles/helpers/removeClickOutline";
import * as timing from "@styles/emotion-styles/timing";
import * as utilities from "@styles/emotion-styles/utilities";

export const accountProfile = {
  base: (theme: Theme) => css`
    transition:
      all ${timing.defaultTiming} ${animations.easeInOutCubic},
      background-color ${timing.defaultTiming} ${timing.moderate} ${animations.easeInOutCubic};
    transform-style: preserve-3d;
    width: 100%;
    display: flex;
    align-items: center;
    cursor: pointer;
    flex-direction: column;
    background-color: ${theme.light.backgroundContrast};
  `,
  open: (theme: Theme) => css`
    ${utilities.transition};
    padding: 0;
    background-color: ${theme.surface.level1};
    flex-direction: column;
    z-index: 1;
    .patient-links {
      padding: 1.25rem 1.5rem 0 1.5rem;
    }
  `,

  ds4: (theme: Theme) => css`
    background-color: ${theme.surface.level0};
  `,
};

export const collapsible = css`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`;

export const collapsibleTrigger = (theme: Theme) => css`
  background-color: ${theme.system.backgroundMuted};
  width: 100%;
  padding: 1rem;
`;

export const collapsibleContent = (theme: Theme) => css`
  background-color: ${theme.system.backgroundMuted};
  width: 100%;
  overflow: hidden;

  @keyframes slideDown {
    from {
      height: 0;
    }
    to {
      height: var(--radix-collapsible-content-height);
    }
  }

  @keyframes slideUp {
    from {
      height: var(--radix-collapsible-content-height);
    }
    to {
      height: 0;
    }
  }

  &[data-state="open"] {
    animation: slideDown 200ms ease-in-out;
  }

  &[data-state="closed"] {
    animation: slideUp 200ms ease-in-out;
  }
`;

export const profile = {
  base: (theme: Theme) => css`
    width: 100%;
    display: flex;
    align-items: center;
    ${removeClickOutline};

    .svg-inline--fa {
      margin-left: auto;
      margin-right: 0;
    }
    .svg-inline--fa path {
      ${utilities.transition};
      fill: ${theme.system.backgroundMuted};
    }
  `,
  open: (theme: Theme) => css`
    .svg-inline--fa path {
      fill: ${theme.success.textBase};
      padding: 1.5rem 1.5rem 0 1.5rem;
    }
  `,
  ds4: (theme: Theme) => css`
    .svg-inline--fa path {
      fill: ${theme.text.emphasis};
    }
  `,
};

export const name = {
  base: (theme: Theme) => css`
    &&& {
      transition: color ${timing.defaultTiming} ${timing.moderate} ${animations.easeInOutCubic};
      color: ${theme.system.backgroundMuted};
      padding: 0 1rem;
      margin: 0;
      width: 13.75rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
  open: (theme: Theme) => css`
    &&& {
      ${utilities.transition};
      color: ${theme.text.body};
    }
  `,

  ds4: (theme: Theme) => css`
    &&& {
      color: ${theme.text.body};
    }
  `,
};

export const signOut = css`
  ${utilities.transition};
  padding: 1rem 1.5rem 1.25rem 1.5rem;
  @media (max-width: ${dimensions.phoneSmallMax}) {
    padding: 1rem;
  }

  @media (max-width: ${dimensions.phoneLargeMax}) and (max-height: 568px) {
    margin-bottom: 1rem;
  }

  @media (max-width: ${dimensions.phoneLargeMax}) and (min-height: 568px) {
    margin-bottom: 2rem;
  }
`;

export const signOutText = css`
  width: 100%;
  display: flex;
`;
